import React from "react";
import { motion } from "framer-motion";
import {
  Award,
  Users,
  Globe,
  TrendingUp,
  Target,
  Shield,
  Eye,
  Heart,
} from "lucide-react";
import { Card, CardContent } from "../ui/Card";
import { Badge } from "../ui/Badge";
import "./AboutPage.css";
import TimelineSection from "./Sections/TimelineSection";
import Achievements from "./Sections/Achievements";
import MetaTagComponent from '../common/Meta/MetaTagComponent'
import { metaDetailsAbout } from "../../Utilites";

const timeline = [
  {
    year: "2019",
    title: "Company Founded",
    description:
      "Started as a small team of passionate developers with a vision to transform businesses through technology.",
  },
  {
    year: "2020",
    title: "First Major Client",
    description:
      "Successfully delivered our first enterprise-level project, establishing our reputation for excellence.",
  },
  {
    year: "2021",
    title: "Team Expansion",
    description:
      "Grew to 25+ experts across development, design, and strategy to serve clients better.",
  },
  {
    year: "2022",
    title: "AI Innovation Lab",
    description:
      "Launched our AI research division, pioneering cutting-edge solutions for clients.",
  },
  {
    year: "2023",
    title: "Global Presence",
    description:
      "Expanded internationally, serving clients across 15+ countries worldwide.",
  },
  {
    year: "2024",
    title: "Industry Recognition",
    description:
      "Received multiple awards for innovation and customer satisfaction.",
  },
];

// const team = [
//   {
//     name: 'Alex Johnson',
//     position: 'CEO & Founder',
//     image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=300&fit=crop&crop=face',
//     bio: '10+ years in tech leadership, former Silicon Valley executive.',
//   },
//   {
//     name: 'Sarah Chen',
//     position: 'CTO',
//     image: 'https://images.unsplash.com/photo-1494790108755-2616b25d4e35?w=300&h=300&fit=crop&crop=face',
//     bio: 'Expert in AI/ML with PhD from Stanford, published researcher.',
//   },
//   {
//     name: 'Michael Rodriguez',
//     position: 'Lead Designer',
//     image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=300&h=300&fit=crop&crop=face',
//     bio: 'Award-winning designer with focus on user-centered experiences.',
//   },
//   {
//     name: 'Emily Davis',
//     position: 'Head of Strategy',
//     image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=300&h=300&fit=crop&crop=face',
//     bio: 'MBA from Wharton, former McKinsey consultant specializing in digital transformation.',
//   },
//   {
//     name: 'David Park',
//     position: 'Senior Developer',
//     image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=300&h=300&fit=crop&crop=face',
//     bio: 'Full-stack expert with 8+ years building scalable applications.',
//   },
//   {
//     name: 'Lisa Thompson',
//     position: 'Project Manager',
//     image: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=300&h=300&fit=crop&crop=face',
//     bio: 'PMP certified with track record of delivering complex projects on time.',
//   },
// ];

const values = [
  {
    icon: Target,
    title: "Excellence",
    description:
      "At MetaFox Tech, we pursue excellence in every detail, ensuring that each project delivers outstanding quality and goes beyond client expectations.",
    gradientClass: "gradient-blue",
  },
  {
    icon: Heart,
    title: "Client‑Centric",
    description:
      "Your goals are our priority. We foster long-term partnerships built on trust, collaboration, and measurable results.",
    gradientClass: "gradient-red",
  },
  {
    icon: TrendingUp,
    title: "Innovation",
    description:
      "We embrace the latest technology trends to craft forward-thinking solutions that help your business stay competitive and future-ready.",
    gradientClass: "gradient-green",
  },
  {
    icon: Shield,
    title: "Integrity",
    description:
      "Honesty and transparency guide our work. From clear communication to fair pricing, we uphold the highest ethical standards.",
    gradientClass: "gradient-purple",
  },
];

const achievements = [
  { icon: Award, number: "15+", label: "Industry Awards" },
  { icon: Users, number: "150+", label: "Happy Clients" },
  { icon: Globe, number: "25+", label: "Countries Served" },
  { icon: TrendingUp, number: "98%", label: "Client Retention" },
];

export default function MFAbout() {
  return (
    <>
    <MetaTagComponent metaDetails={metaDetailsAbout} />
    <div className="about-page">
      {/* Hero Section */}
      <section className="hero-section text-light position-relative d-flex align-items-center justify-content-center">
        <div className="container text-center">
          <Badge className="custom-badge-light text-primary mb-2">
            About MetaFox
          </Badge>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="display-4 fw-bold gradient-text">
              Driving Digital Growth with Purpose
            </h1>
            <p className="lead mt-3 text-muted fw-500">
              MetaFox Tech blends creativity, strategy, and technology to build
              impactful digital solutions. From startups to enterprises, we help
              brands scale with custom web development, mobile apps, and
              scalable digital products.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Our Purpose & Values */}
      <section className="py-5 bg-white">
        <div className="container text-center">
          <div className="mb-5">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="fw-bold gradient-text">Our Purpose & Values</h2>
            </motion.div>
          </div>

          <div className="row gx-4 gy-4">
            {/* Mission */}
            <div className="col-lg-4 col-md-6 ">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                <Card className="h-100 card-gradient-blue border-0 shadow-sm text-center">
                  <CardContent>
                    <Target size={48} className="mb-3 text-primary" />
                    <h5 className="fw-bold text-primary">Our Mission</h5>
                    <p className="text-muted">
                      Our mission at MetaFox Tech is to empower businesses with
                      innovative technology solutions that accelerate growth,
                      enhance efficiency, and create lasting competitive
                      advantages in the digital era.
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
            {/* Vision */}
            <div className="col-lg-4 col-md-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <Card className="h-100 border-0 card-gradient-purple shadow-sm text-center">
                  <CardContent>
                    <Eye size={48} className="mb-3 text-purple" />
                    <h5 className="fw-bold text-purple">Our Vision</h5>
                    <p className="text-muted">
                      We aim to be the trusted partner for businesses seeking
                      transformative technology solutions that drive innovation
                      and shape the future of digital success.
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
            {/* Values */}
            <div className="col-lg-4 col-md-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <Card className="h-100 border-0 card-gradient-green shadow-sm text-center">
                  <CardContent>
                    <Heart size={48} className="mb-3 text-success" />
                    <h5 className="fw-bold text-success">Our Values</h5>
                    <p className="text-muted">
                      At MetaFox Tech, we value innovation, integrity,
                      excellence, and above all, the success of our clients.
                      Building strong, lasting partnerships is at the heart of
                      everything we do.
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          </div>

          {/* Core Values */}
          <div className="row gx-4 gy-4 mt-5">
            {values.map((value, idx) => {
              const IconComp = value.icon;
              return (
                <div key={value.title} className="col-lg-3 col-md-6">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: idx * 0.1 }}
                    whileHover={{ y: -5 }}
                  >
                    <Card className="h-100 border-0 shadow-sm text-center pt-3 value-card">
                      <div
                        className={`icon-wrapper ${value.gradientClass} rounded-circle d-flex align-items-center justify-content-center mx-auto`}
                      >
                        <IconComp size={32} className="text-white" />
                      </div>
                      <CardContent>
                        <h5 className="fw-bold">{value.title}</h5>
                        <p className="text-muted small">{value.description}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <TimelineSection timeline={timeline} />

      {/* Team Section */}
      {/* <TeamSection team={team} /> */}

      {/* Achievements Section */}
      <Achievements achievements={achievements} />
    </div>
    </>
  );
}
